import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import { getCdnImage } from '../lib/imageUtils';

export default function ServicesOverview() {
  const services = [
    {
      title: 'Vandværksservice & ledningsarbejde',
      slug: 'vandvaerksservice-ledningsarbejde',
      desc: 'Professionelt gravearbejde og vedligeholdelse for vandværker. Hurtig og effektiv indsats ved ledningsbrud og rørskader.',
      points: [
        'Akut hjælp ved ledningsbrud og rørskader',
        'Nedgravning og udskiftning af målerbrønde',
        'Afpropning af stikledninger',
        'Skydning med jordraket',
      ],
      icon: 'fa-solid fa-faucet-drip',
      image: 'https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-605221099-1366670058493953-1062181551945056561-n.jpg',
    },
    {
      title: 'Autoriseret kloakarbejde & tilslutning',
      slug: 'kloakarbejde-tilslutning',
      desc: 'Alt inden for autoriseret kloakmesterarbejde til private og erhverv. Sikkerhed og garanti i samarbejde med Byggeriets Kvalitetskontrol.',
      points: [
        'Komplet kloaktilslutning ved nybyggeri, renovering og separering',
        'Etablering og montering af godkendte pumpestationer og samletanke',
        'Montering af rottespærrer',
        'Fejlfinding og reparation af kloaksystemer',
      ],
      icon: 'fa-solid fa-water',
      image: 'https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-601820261-1366670001827292-7586379579870585065-n.jpg',
    },
    {
      title: 'Belægning & anlæg',
      slug: 'belaegning-anlaeg',
      desc: 'Holdbare og professionelle belægningsløsninger til indkørsler, terrasser og erhvervsarealer med fokus på korrekt bundopbygning.',
      points: [
        'Lægning af alle typer belægningssten, herunder herregårdssten og fliser',
        'Professionel bundopbygning og afretning',
        'Stabilt underlag, der mindsker risikoen for sætninger',
        'Levering og kørsel af stabilgrus, flisesand, sten og granitskærver',
      ],
      icon: 'fa-solid fa-layer-group',
      image: 'https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-481666921-1140782317749396-168441956002480996-n.jpg',
    },
    {
      title: 'Vinterservice & snerydning',
      slug: 'vinterservice-snerydning',
      desc: 'Pålidelig snerydning og saltning i vintermånederne for forretninger, virksomheder og institutioner på Lolland-Falster.',
      points: [
        'Snerydning af parkeringspladser og tilkørselsveje',
        'Saltning af parkeringspladser og tilkørselsveje',
        'Vinterservice for forretninger, virksomheder og institutioner',
        'Faste vinteraftaler eller tilkald ved snefald',
      ],
      icon: 'fa-solid fa-snowflake',
      image: 'https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/imgi-94-613121285-1380287927132166-3828401678356812271-n.jpg',
    },
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      <SeoMeta
        title="Ydelser - ENTREPRENØR LANG ApS"
        description="Entreprenør Lang ApS tilbyder vandværksservice, autoriseret kloakarbejde, belægning og vinterservice på Lolland-Falster."
        keywords="kloak, gravearbejde, snerydning, glatførebekæmpelse, vandværk, belægning"
      />

      {/* Hero Banner */}
      <section className="bg-slate-900 text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={getCdnImage('https://assets.auxodev.dk/assets/7d468145-c8f2-4ea0-936d-cc188f08bb8a/hero-entreprenor-gravemaskine.jpg', {
              width: 1920,
              quality: 80,
            })}
            alt="Ydelser baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-3 border border-emerald-500/30">
              <i className="fa-solid fa-list-check" aria-hidden="true" />
              Vores ekspertise
            </span>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
              Hvad laver vi
            </h1>
            <p className="text-slate-300 text-base sm:text-lg mt-3 leading-relaxed">
              Vi løser en bred vifte af entreprenøropgaver for både forsyningsselskaber, erhvervskunder og private med stor præcision og moderne udstyr.
            </p>
          </div>
        </div>
      </section>

      {/* Scrape Context Card */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-20">
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-md border border-slate-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center flex-shrink-0">
              <i className="fa-solid fa-quote-left text-xl" aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                Om udviklingen i vores arbejdsområder
              </h3>
              <p className="text-slate-700 text-sm sm:text-base mt-2 leading-relaxed">
                Da vi startede i 2005 var kloak og drænarbejde samt jord og beton og belægning, men det har ændret sig lidt da der er blevet meget gravearbejde for vandværker ved brud, skiftning af målerbrønde, afpropning af stik osv.
              </p>
              <p className="text-slate-700 text-sm sm:text-base mt-2 leading-relaxed">
                Udover kloak- og drænarbejde har vi også meget saltning og snerydning i vintermånederne ved parkeringsarealer for forretninger og virksomheder.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="space-y-12">
          {services.map((svc, idx) => {
            const isEven = idx % 2 === 1;
            return (
              <div
                key={svc.slug}
                className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center ${isEven ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={`lg:col-span-5 h-72 lg:h-full min-h-[300px] relative overflow-hidden bg-slate-100 ${isEven ? 'lg:order-2' : ''}`}>
                    <img
                      src={getCdnImage(svc.image, { width: 800, height: 600, fit: 'cover' })}
                      alt={svc.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  <div className={`lg:col-span-7 p-6 sm:p-8 lg:p-10 ${isEven ? 'lg:order-1' : ''}`}>
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                      {svc.title}
                    </h2>

                    <p className="text-slate-600 text-sm sm:text-base mt-3 leading-relaxed">
                      {svc.desc}
                    </p>

                    <div className="mt-6 space-y-2.5">
                      {svc.points.map((pt, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <i className="fa-solid fa-circle-check text-emerald-600 text-sm mt-1" aria-hidden="true" />
                          <span className="text-slate-800 text-sm font-medium">{pt}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-4">
                      <Link
                        to={`/ydelser/${svc.slug}`}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm shadow transition-all hover:-translate-y-0.5"
                      >
                        <span>Gå til {svc.title}</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </Link>
                      <Link
                        to="/kontakt-os"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-sm transition-colors"
                      >
                        <span>Få et tilbud</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <section className="bg-emerald-800 text-white py-14 px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-4xl mx-auto space-y-4">
          <h3 className="text-2xl sm:text-3xl font-extrabold">
            Har du en specifik opgave på hånden?
          </h3>
          <p className="text-emerald-100 text-base max-w-2xl mx-auto">
            Vi rådgiver gerne om den bedste tekniske løsning og giver et uforpligtende tilbud til private, erhverv og forsyninger.
          </p>
          <div className="pt-4 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt-os"
              className="px-8 py-3.5 rounded-xl bg-white text-slate-900 font-bold text-sm hover:bg-slate-100 shadow transition-all"
            >
              Udfyld kontaktformular
            </Link>
            <a
              href="tel:40309708"
              className="px-8 py-3.5 rounded-xl bg-emerald-950 text-white font-bold text-sm hover:bg-emerald-900 transition-colors whitespace-nowrap border border-emerald-700"
            >
              <i className="fa-solid fa-phone mr-2 text-emerald-400" aria-hidden="true" />
              Ring 40 30 97 08
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
